import { useCallback, useEffect, useRef, useState } from 'react';
import { listReports, type ReportsPage, type ReportsQuery } from './api';
import type { Person, ReportSummary } from './types';

export type ReportsListState =
  | { status: 'loading' }
  | { status: 'error' }
  | {
      status: 'ready';
      items: readonly ReportSummary[];
      total: number;
      owners: readonly Person[];
      can: ReportsPage['can'];
    };

/** Fetches one offset page, replacing rows whenever the query changes. */
export function useReportsList(query: ReportsQuery) {
  const [state, setState] = useState<ReportsListState>({ status: 'loading' });
  const [reloads, setReloads] = useState(0);
  const key = JSON.stringify(query);
  const generation = useRef(0);

  useEffect(() => {
    const mine = ++generation.current;
    setState({ status: 'loading' });
    void listReports(JSON.parse(key) as ReportsQuery).then((outcome) => {
      if (mine !== generation.current) return;
      if (!outcome.ok) {
        setState({ status: 'error' });
        return;
      }
      const page = outcome.value;
      setState({
        status: 'ready',
        items: page.items,
        total: page.total,
        owners: page.owners,
        can: page.can,
      });
    });
  }, [key, reloads]);

  const reload = useCallback(() => setReloads((count) => count + 1), []);
  return { state, reload } as const;
}
